import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useTenantStore } from "../../stores/tenant";
import { useToastStore } from "../../stores/toast";

interface KbEntry {
  id: string;
  tenantId: string;
  category: string;
  question: string;
  answer: string;
  priority: number;
}

// Catégories que l'agent vocal interroge via lookup_kb.
const CATEGORIES = ["horaires", "menu", "prix", "politique", "faq", "autre"];

export function KbList() {
  const tenantId = useTenantStore((s) => s.currentTenantId);
  const push = useToastStore((s) => s.push);
  const [entries, setEntries] = useState<KbEntry[]>([]);
  const [category, setCategory] = useState("horaires");
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => {
    if (!tenantId) return;
    api<KbEntry[]>(`/api/tenants/${tenantId}/kb`).then(setEntries).catch(() => setEntries([]));
  };

  useEffect(load, [tenantId]);

  const add = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!tenantId || !question.trim() || !answer.trim() || saving) return;
    setSaving(true);
    try {
      await api(`/api/tenants/${tenantId}/kb`, {
        method: "POST",
        body: JSON.stringify({ category, question: question.trim(), answer: answer.trim(), priority: 0 }),
      });
      setQuestion("");
      setAnswer("");
      push("success", "Info ajoutée");
      load();
    } catch {
      push("danger", "Échec de l'ajout");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: string) => {
    if (!tenantId) return;
    try {
      await api(`/api/tenants/${tenantId}/kb/${id}`, { method: "DELETE" });
      setEntries((prev) => prev.filter((x) => x.id !== id));
    } catch {
      push("danger", "Échec de la suppression");
    }
  };

  if (!tenantId) return <div className="page-header"><h1 className="page-header__title">Sélectionne un établissement</h1></div>;

  return (
    <div>
      <header className="page-header">
        <div className="page-header__titles">
          <h1 className="page-header__title">Infos / FAQ</h1>
          <p className="page-header__subtitle">
            Ce que l'agent peut répondre : horaires, menu, options, politiques, FAQ.
          </p>
        </div>
      </header>
      <form className="config-form" onSubmit={add}>
        <select className="select" value={category} onChange={(e) => setCategory(e.target.value)}>
          {CATEGORIES.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        <input
          className="input"
          type="text"
          placeholder="Question (ex : Êtes-vous ouverts le dimanche ?)"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
        />
        <input
          className="input config-form__grow"
          type="text"
          placeholder="Réponse (ex : Oui, de 10 h à 14 h.)"
          value={answer}
          onChange={(e) => setAnswer(e.target.value)}
        />
        <button type="submit" className="btn btn--primary" disabled={saving || !question.trim() || !answer.trim()}>
          {saving ? "Ajout…" : "Ajouter"}
        </button>
      </form>

      <table className="table">
        <thead>
          <tr>
            <th>Catégorie</th>
            <th>Question</th>
            <th>Réponse</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {entries.length === 0 ? (
            <tr><td colSpan={4} className="table__empty">Aucune info — ajoute-en pour que l'agent puisse répondre.</td></tr>
          ) : (
            entries.map((e) => (
              <tr key={e.id}>
                <td>{e.category}</td>
                <td>{e.question}</td>
                <td>{e.answer}</td>
                <td>
                  <button type="button" className="btn btn--ghost btn--sm" onClick={() => void remove(e.id)}>
                    Supprimer
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

export default KbList;
